import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { questionById, SETUP_QUESTIONS, SetupQuestion, SetupScreenId } from '../../../config/setupQuestions';
import { ScheduleEntry } from '../../../types';
import { getAnswer, setupProgress } from '../../../utils/setupAnswers';
import QuestionEditor, { useFormatRule, useQuestionTitle } from './QuestionEditor';
import { useNextDateLabel } from './YearSetupWizard';

const GROUPS: { key: string; screens: SetupScreenId[] }[] = [
  { key: 'groupEveryWeek', screens: ['weekday', 'monThu', 'friday', 'shabbos'] },
  { key: 'groupMonthly', screens: ['roshChodesh', 'chanukah', 'fasts', 'purim', 'beinHazmanim'] },
  { key: 'groupTishrei', screens: ['selichot', 'yomTov', 'roshHashana', 'yomKippur', 'sukkot'] },
  { key: 'groupSpring', screens: ['pesach', 'shavuot', 'tishaBav'] },
];

interface YearChecklistProps {
  schedules: ScheduleEntry[];
  onSave: (next: ScheduleEntry[]) => Promise<void>;
}

/** B · Year checklist: every kind of day with its answers; tap an answer to change it. */
export default function YearChecklist({ schedules, onSave }: YearChecklistProps) {
  const { t } = useTranslation();
  const formatRule = useFormatRule();
  const titleOf = useQuestionTitle();
  const nextDateLabel = useNextDateLabel();
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [editing, setEditing] = useState<SetupQuestion | null>(null);
  const [draft, setDraft] = useState<ScheduleEntry[]>(schedules);
  const [saving, setSaving] = useState(false);

  const progress = setupProgress(schedules);
  const pct = Math.round((progress.answered / progress.total) * 100);

  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEditing(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editing]);

  const open = (q: SetupQuestion) => {
    setDraft(schedules);
    setEditing(q);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (draft !== schedules) await onSave(draft);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const chips = (q: SetupQuestion) => {
    const a = getAnswer(schedules, q);
    const base = 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums whitespace-nowrap';
    if (a.mode === 'unset') return [<span key="u" className={`${base} bg-gold-50 text-gold-700 ring-1 ring-gold-600`}>{t('setup.ui.notSet')}</span>];
    if (a.mode === 'none') return [<span key="n" className={`${base} bg-fog text-ash line-through`}>{t('setup.ui.noMinyan')}</span>];
    if (a.mode === 'same') {
      const linked = q.sameAs ? questionById(q.sameAs) : undefined;
      return [<span key="s" className={`${base} bg-fog text-ash`}>= {linked ? titleOf(linked) : ''}</span>];
    }
    return a.entries.map((e) => (
      <span key={e.id} className={`${base} bg-weekday-50 text-weekday-700`}>{formatRule(e.timeRule)}</span>
    ));
  };

  return (
    <div className="space-y-4">
      <div className="rounded-[16px] border border-ink/10 bg-paper p-4 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-ink">{t('setup.ui.checklistTitle')}</h3>
          <span className="text-xs text-ash">{t('setup.ui.answeredOf', { answered: progress.answered, total: progress.total })}</span>
        </div>
        <div className="h-2 rounded-full bg-fog overflow-hidden" aria-hidden="true">
          <div className="h-full bg-weekday-600 rounded-full" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex gap-2 pt-1">
          {[false, true].map((missing) => (
            <button
              key={String(missing)}
              type="button"
              aria-pressed={onlyMissing === missing}
              onClick={() => setOnlyMissing(missing)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                onlyMissing === missing ? 'bg-ink text-paper border-ink' : 'border-ink/20 text-ink'
              }`}
            >
              {missing ? t('setup.ui.onlyMissing') : t('setup.ui.everything')}
            </button>
          ))}
        </div>
      </div>

      {onlyMissing && progress.answered === progress.total && (
        <p className="text-sm text-ash">{t('setup.ui.nothingMissing')}</p>
      )}

      {GROUPS.map((group) => {
        const screens = group.screens.filter((s) => !onlyMissing || (progress.missingByScreen[s] ?? 0) > 0);
        if (!screens.length) return null;
        return (
          <section key={group.key} className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-ash">{t(`setup.ui.${group.key}`)}</h4>
            {screens.map((s) => {
              const missing = progress.missingByScreen[s] ?? 0;
              const qs = SETUP_QUESTIONS.filter((q) => q.screen === s && (!onlyMissing || getAnswer(schedules, q).mode === 'unset'));
              return (
                <details key={s} open={onlyMissing || undefined} className="rounded-[16px] border border-ink/10 bg-paper overflow-hidden">
                  <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-ink">{t(`setup.screens.${s}.title`)}</span>
                      <span className="block text-xs text-ash">{nextDateLabel(s)}</span>
                    </span>
                    <span
                      className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                        missing ? 'bg-gold-50 text-gold-700' : 'bg-weekday-50 text-weekday-700'
                      }`}
                    >
                      {missing ? t('setup.ui.missingCount', { count: missing }) : t('setup.ui.statusDone')}
                    </span>
                  </summary>
                  <ul className="divide-y divide-ink/10 border-t border-ink/10">
                    {qs.map((q) => (
                      <li key={q.id}>
                        <button
                          type="button"
                          onClick={() => open(q)}
                          className="w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-4 py-2.5 text-start hover:bg-fog"
                        >
                          <span className="text-sm text-ink min-w-0">{titleOf(q)}</span>
                          <span className="flex flex-wrap justify-end gap-1">{chips(q)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              );
            })}
          </section>
        );
      })}

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-ink/40 flex items-end sm:items-center justify-center"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setEditing(null);
          }}
        >
          <div role="dialog" aria-modal="true" aria-label={titleOf(editing)} className="w-full sm:max-w-lg max-h-[88vh] overflow-y-auto bg-fog rounded-t-[20px] sm:rounded-[20px] p-4 space-y-3">
            <p className="text-xs text-ash">
              {t(`setup.screens.${editing.screen}.title`)} · {nextDateLabel(editing.screen)}
            </p>
            <QuestionEditor q={editing} schedules={draft} onChange={setDraft} />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-[10px] border border-ink/20 text-sm font-medium text-ink"
              >
                {t('setup.ui.cancel')}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void save()}
                className="flex-1 px-4 py-2 rounded-[10px] bg-ink text-paper text-sm font-medium disabled:opacity-50"
              >
                {saving ? t('setup.ui.saving') : t('setup.ui.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
